import { Injectable } from '@angular/core';
import { Mutex } from 'async-mutex';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  firstValueFrom,
  map,
  of,
  startWith,
  switchMap,
  tap,
  catchError,
} from 'rxjs';
import { filterNil } from '@ngneat/elf';
import { TranslateService } from '@ngx-translate/core';
import { refinement } from 'ts-adt';
import { Collaboration, AccessControl } from '@pncl/common-models';
import {
  PauseableRecordingService,
  RECORDING_ERROR,
  RECORDING_STATE,
  RECORD_STATUS,
  RecordingOutput,
  RecordingService,
} from '../models/recording';
import { SpaceRepository } from '../state/space.repository';
import { RecordingMode, User } from '../models/user';
import { ISession, Session } from '../models/session';
import { PresenceRepository } from '../state/presence.repository';
import { RecordingAccess, RecordingSummary, SpaceRecording } from '../models/space-recording';
import { ERRORS, SUCCESSES } from '../common/utils/notification-constants';
import { ConfirmationModalComponent } from '../standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { markRecordingForDeletionOnNextOpen } from '../sessions/session/wb-video-controls/recording-db';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { SpaceLocalRecordingService } from './space-local-recording.service';
import { SpaceCloudRecordingService } from './space-cloud-recording.service';
import { UserService } from './user.service';
import { ProviderStateService } from './provider-state.service';
import { SessionCallParticipantsService } from './session-call-participants.service';
import { PresenceProvider } from './presence-provider';
import { TelemetryService } from './telemetry.service';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';
import { DownloadCapabilities, FeatureManagementService } from './feature-management.service';
import { DevicesManagerService } from './devices-manager.service';
import { SpacesService } from './spaces.service';
import { NotificationToasterService } from './notification-toaster.service';
import { ModalManagerService } from './modal-manager.service';
import { CapabilityTierService } from './capability-tier.service';
import { FLAGS, FlagsService } from './flags.service';

import Capability = AccessControl.Capability;

export enum RECORDING_SERVICES {
  LOCAL,
  CLOUD,
}

// Chrome silently drops extra downloads that start too close together. Keep the
// anchor in the DOM for this gap so the navigation is not cancelled on remove.
const BROWSER_DOWNLOAD_THROTTLE_MS = 1000;

export enum RECORDING_DETAILS_NAVIGATION_SOURCE {
  DASHBOARD = 'DASHBOARD',
  ANALYTICS_OUTSIDE_SPACE = 'ANALYTICS_OUTSIDE_SPACE',
  ANALYTICS_INSIDE_SPACE = 'ANALYTICS_INSIDE_SPACE',
  COPY_LINK = 'COPY_LINK',
}

export interface RecordingViewPermissions {
  download: boolean;
  /** Same role rule as `download`, but gated on CLOUD_RECORDING instead of DOWNLOAD_CLOUD_RECORDINGS */
  downloadTranscript: boolean;
  delete: boolean;
  changeRecordingAccess: boolean;
  publicAccessEnabled?: boolean;
  adminAccessEnabled?: boolean;
  editSummary?: boolean;
  showViewSpaceButton?: boolean;
  showCopyLinkButton?: boolean;
  showHeader?: boolean;
}

export interface RecordingAccessUpdate {
  recordingId: string;
  access: RecordingAccess;
}

export enum RecordingDetailsPanelTabs {
  SUMMARY = 'summary',
  TRANSCRIPT = 'transcript',
  INSIGHTS = 'insights',
}

export interface RecordingAccessOption {
  value: RecordingAccess;
  label: string;
  subtitle?: string;
  disabled?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SpaceRecordingService {
  private _didCurrentUserEndRecording$ = new Subject();
  private _recordingOutput$: BehaviorSubject<RecordingOutput> =
    new BehaviorSubject<RecordingOutput>({ _type: 'none' });
  private currentService$: Observable<RecordingService>;
  private recordingEndedReason: string | undefined;
  /** When true, the next finished local recording is auto-downloaded, skipping the save/upload modal. */
  private _autoDownloadNextLocalRecording = false;
  private recordingInitiator$: Observable<string>;
  private services: Map<RECORDING_SERVICES, RecordingService> = new Map();
  private status$ = new BehaviorSubject<RECORDING_STATE>({ _type: RECORD_STATUS.READY });
  public readonly currentRecorder$ = new BehaviorSubject<RECORDING_SERVICES>(
    RECORDING_SERVICES.LOCAL,
  );
  public readonly didCurrentUserEndRecording$ = this._didCurrentUserEndRecording$.asObservable();
  public readonly didCurrentUserStartRecording$ = new BehaviorSubject<boolean>(false);
  public readonly isAnyoneRecording$: Observable<boolean>;
  public readonly isCurrentUserRecordingInProgress$: Observable<boolean>;
  public readonly isCurrentUserRecordingPauseable$: Observable<boolean>;
  public readonly isCurrentUserRecordingPaused$: Observable<boolean>;
  public readonly isRecordingInitiatorPresent$: Observable<boolean>;
  public shouldShowRecordingNotification$: Observable<boolean>;
  public readonly recordingOutput$ = this._recordingOutput$.asObservable().pipe(
    map((output) => {
      switch (output._type) {
        case 'file':
          output.reason = this.recordingEndedReason;
          this.recordingEndedReason = undefined;
      }
      return output;
    }),
  );
  private isAutomaticCloudRecordingEnabledSubject = new BehaviorSubject<boolean>(false);
  public readonly isAutomaticCloudRecordingEnabled$ =
    this.isAutomaticCloudRecordingEnabledSubject.asObservable();

  private updatedRecordingsListSubject$ = new Subject<RecordingAccessUpdate[]>();
  public updatedRecordingsList$ = this.updatedRecordingsListSubject$.asObservable();

  readonly RecordingAccessEnumToTextMapping: Record<RecordingAccess, string> = {
    [RecordingAccess.HOSTS]: this.translateService.instant('Hosts only'),
    [RecordingAccess.HOSTS_AND_PARTICIPANTS]:
      this.translateService.instant('Hosts and participants'),
    [RecordingAccess.PUBLIC]: this.translateService.instant('Public'),
    [RecordingAccess.ADMINS]: this.translateService.instant('Admins only'),
  };

  private readonly recordingAccessSubtitles: Record<RecordingAccess, string> = {
    [RecordingAccess.HOSTS]: this.translateService.instant('Only hosts can access'),
    [RecordingAccess.HOSTS_AND_PARTICIPANTS]: this.translateService.instant(
      'Anyone in the Space can access',
    ),
    [RecordingAccess.PUBLIC]: this.translateService.instant('Anyone with a link can access'),
    [RecordingAccess.ADMINS]: this.translateService.instant('Only admins can access'),
  };

  public getRecordingAccessOptions(options?: {
    includeSubtitles?: boolean;
  }): RecordingAccessOption[] {
    const { includeSubtitles = false } = options || {};

    return Object.values(RecordingAccess).map((access) => ({
      value: access,
      label: this.RecordingAccessEnumToTextMapping[access],
      subtitle: includeSubtitles ? this.recordingAccessSubtitles[access] : undefined,
    }));
  }

  // Privacy aware cloud recording behavior subject
  private isPrivacyAwareCloudRecordingEnabledSubject = new BehaviorSubject<boolean>(false);
  public readonly isPrivacyAwareCloudRecordingEnabled$ =
    this.isPrivacyAwareCloudRecordingEnabledSubject.asObservable();

  public cloudRecordingActive$ = this.cloudRecordingService.state$.pipe(
    map((recordingState) => recordingState._type === RECORD_STATUS.ACTIVE),
  );

  // RecordingMode.DISABLED should be used to disable the entire recording feature for the space
  // crRecordingMode will be left as is for now to avoid backward compatibility issues
  private _isRecordingDisabledByInstitution(space: ISession) {
    return space?.institution?.settings?.crRecordingMode === RecordingMode.DISABLED;
  }

  public readonly isRecordingDisabledByInstitution$ = this.spaceRepo.activeSpace$.pipe(
    filterNil(),
    map((space) => this._isRecordingDisabledByInstitution(space)),
    distinctUntilChanged(),
  );

  private setSpaceRecordingVars(recording: boolean): void {
    const cloud_recording_active = recording && !this.isCurrentRecordingLocal();
    const local_recording_active = recording && this.isCurrentRecordingLocal();

    this.telemetry.setSessionVars({
      cloud_recording_active,
      local_recording_active,
      local_recording_user_is_recording: recording && this.didCurrentUserStartRecording$.getValue(),
    });
  }

  public get isAutomaticCloudRecordingEnabled(): boolean {
    return this.isAutomaticCloudRecordingEnabledSubject.getValue();
  }

  public get forceShareScreen(): boolean {
    return (
      this.isAutomaticCloudRecordingEnabled &&
      !!this.spaceRepo.activeSpace?.institution?.settings?.forceScreenShareInAutomaticCr &&
      !this.flagsService.isFlagEnabled(FLAGS.RECORD_WB_ON_CLOUD)
    );
  }

  /**
   * @description
   * This observable emits true if the user can start a recording manually,
   * including local recording option
   */
  public readonly canUserControlRecordingManually$: Observable<boolean> =
    this.devicesManagerService.isSharingScreenSupported()
      ? combineLatest([
          this.spaceRepo.activeSpace$.pipe(filterNil()),
          this.userService.user.pipe(
            filterNil(),
            map(({ user }) => user),
          ),
        ]).pipe(map(([space, user]) => this._canUserControlRecordingManually(space, user)))
      : of(false);

  public readonly didCurrentRecordingTimeOut$: Observable<boolean> = this.status$.pipe(
    filter(refinement(RECORD_STATUS.FINISHED)),
    map((s) => s.err?.type === RECORDING_ERROR.IDLE_TIMEOUT),
    distinctUntilChanged(),
  );

  public get isPrivacyCloudRecordingEnabled(): boolean {
    return this.isPrivacyAwareCloudRecordingEnabledSubject.getValue();
  }
  private readonly _isDownloading$ = new BehaviorSubject<boolean>(false);
  public readonly isDownloading$ = this._isDownloading$.asObservable();
  private readonly browserDownloadMutex = new Mutex();
  private readonly _isDownloadingTranscript$ = new BehaviorSubject<boolean>(false);
  public readonly isDownloadingTranscript$ = this._isDownloadingTranscript$.asObservable();

  private readonly _recordingDetailsNavigationSource$ = new BehaviorSubject<
    RECORDING_DETAILS_NAVIGATION_SOURCE | undefined
  >(RECORDING_DETAILS_NAVIGATION_SOURCE.COPY_LINK);

  public readonly recordingDetailsNavigationSource$ =
    this._recordingDetailsNavigationSource$.asObservable();

  // This is only relevant for the Recording details view inside the Analytics section.
  // Do not use it when the RecordingDetailsComponent is active in other contexts
  // like the dashboard view or copied link scenarios.
  private readonly _recordingDetailsViewActive$ = new BehaviorSubject<boolean>(false);
  public readonly recordingDetailsViewActive$ = this._recordingDetailsViewActive$.asObservable();

  /** `capabilityEnabled` is DOWNLOAD_CLOUD_RECORDINGS, which gates the recording download. */
  getDownloadRecordingTooltipText(capabilityEnabled: boolean, downloadEnabled: boolean): string {
    if (!capabilityEnabled) {
      return this.translateService.instant(
        'Downloading recordings is not part of your current plan',
      );
    } else if (!downloadEnabled) {
      return this.translateService.instant('You don’t have access to download this recording');
    } else {
      return this.translateService.instant(
        'Download will be available after recording has been processed',
      );
    }
  }

  /** `capabilityEnabled` is CLOUD_RECORDING, which gates the transcript download. */
  getDownloadTranscriptTooltipText(
    capabilityEnabled: boolean,
    downloadEnabled: boolean,
    hasTranscript?: boolean,
  ): string {
    if (!capabilityEnabled) {
      return this.translateService.instant(
        'Downloading transcripts is not part of your current plan',
      );
    } else if (!downloadEnabled) {
      return this.translateService.instant('You don’t have access to download this transcript');
    } else if (!hasTranscript) {
      return this.translateService.instant('No transcription available');
    } else {
      return this.translateService.instant(
        'Download will be available after recording has been processed',
      );
    }
  }

  constructor(
    private cloudRecordingService: SpaceCloudRecordingService,
    private localRecordingService: SpaceLocalRecordingService,
    private presenceProvider: PresenceProvider,
    private presenceRepository: PresenceRepository,
    private providerStateService: ProviderStateService,
    private sessionCallParticipantsService: SessionCallParticipantsService,
    private spaceRepo: SpaceRepository,
    private translateService: TranslateService,
    private userService: UserService,
    private telemetry: TelemetryService,
    private browserDetectionService: DeviceAndBrowserDetectorService,
    private featureManagementService: FeatureManagementService,
    private devicesManagerService: DevicesManagerService,
    private spacesService: SpacesService,
    private notificationToasterService: NotificationToasterService,
    private modalManagerService: ModalManagerService,
    private capabilityTierService: CapabilityTierService,
    private flagsService: FlagsService,
  ) {
    // ---- Private Initializers ----
    this.services.set(RECORDING_SERVICES.LOCAL, this.localRecordingService);
    this.services.set(RECORDING_SERVICES.CLOUD, this.cloudRecordingService);

    this.currentService$ = this.currentRecorder$.pipe(
      map((recordingService) => {
        const service = this.services.get(recordingService);
        if (!service) {
          throw new Error('Expected the service from currentRecorder$ to be in the services map');
        }
        return service;
      }),
    );

    this.recordingInitiator$ = this.status$.pipe(
      filter(refinement(RECORD_STATUS.ACTIVE, RECORD_STATUS.PAUSED)),
      map((x) => x.initiatorParticipantId),
      filterNil(),
    );

    // ---- Public Initializers ----
    this.currentService$
      .pipe(
        switchMap((service) => service.state$),
        distinctUntilChanged(),
        tap(this.handleRecordingStatusChange.bind(this)),
      )
      .subscribe(this.status$);

    combineLatest([this.recordingInitiator$, this.providerStateService.localParticipantId$])
      .pipe(map(([initiator, currentParticipant]) => initiator === currentParticipant))
      .subscribe(this.didCurrentUserStartRecording$);

    this.isRecordingInitiatorPresent$ = combineLatest([
      this.recordingInitiator$,
      this.sessionCallParticipantsService.participants$,
    ]).pipe(
      map(([initiator, participants]) => initiator in participants),
      startWith(false),
    );

    this.isCurrentUserRecordingInProgress$ = this.status$.pipe(map(this._isRecordingInProgress));
    this.isCurrentUserRecordingPaused$ = this.status$.pipe(
      map((status) => status._type === RECORD_STATUS.PAUSED),
    );
    this.isCurrentUserRecordingPauseable$ = this.currentService$.pipe(
      map((service) => PauseableRecordingService.isPauseable(service)),
    );

    // ---- Business Logic ----

    // If a cloud recording is enabled we should stop any local recording
    // Note: we also switch the service to be cloud recording so that the
    //       observables will emit data from the correct output
    this.cloudRecordingService.state$.subscribe(async (crStatus) => {
      if (
        crStatus._type === RECORD_STATUS.ACTIVE &&
        this.currentRecorder$.getValue() !== RECORDING_SERVICES.CLOUD
      ) {
        // If there is a recording active for a provider that is not cloud, then we should end it
        if (this.isRecordingInProgress()) {
          this.recordingEndedReason = this.translateService.instant(
            'Your recording was completed because cloud recording was enabled',
          );
          await this.stopRecording();
        }
        // Then we switch the service to be the cloud recording service
        this.switchService(RECORDING_SERVICES.CLOUD);
      }
    });

    this.currentService$.pipe(switchMap((x) => x.output$)).subscribe(this._recordingOutput$);

    // Listen to see if any remote user starts a recording

    this.isAnyoneRecording$ = combineLatest([
      this.spaceRepo.activeSpaceId$.pipe(filterNil()),
      this.spaceRepo.activeSpaceCurrentRoomUid$.pipe(filterNil()),
      this.providerStateService.callConnected$,
    ]).pipe(
      switchMap(([spaceId, roomUid, connected]) => {
        if (connected) {
          return combineLatest([
            this.isCurrentUserRecordingInProgress$,
            this.presenceRepository.getRecordingPresence(spaceId, roomUid),
          ]).pipe(map((x) => x.includes(true)));
        }
        return of(false);
      }),
      distinctUntilChanged(),
      tap((recording) => this.setSpaceRecordingVars(recording)),
    );

    // If a cloud recording times out, automatically restart it
    this.currentRecorder$
      .pipe(
        switchMap((service) =>
          service === RECORDING_SERVICES.CLOUD ? this.didCurrentRecordingTimeOut$ : EMPTY,
        ),
        filter((didRecordingTimeout) => didRecordingTimeout === true),
      )
      .subscribe(() => {
        // @TODO (mfmansoo) - there is a minor issue with this solution. Currently we only allow
        //                    the initiator of the cloud recording to start / stop the cloud recording.
        //                    There is no good way to figure out if the initiator is still in the call
        //                    when we recover the cloud recording.
        console.warn(
          'Cloud recording unexpectedly timed out, automatically restarting the recording',
        );
        if (this.currentRecorder$.getValue() !== RECORDING_SERVICES.CLOUD) {
          this.switchService(RECORDING_SERVICES.CLOUD);
        }
        this._startRecording();
      });

    // If automatic recording is enabled, we should only show the notification the first
    // time the user joins the call. If the user switches rooms we should NOT show the
    // notification again
    this.shouldShowRecordingNotification$ = this.spaceRepo.activeSpaceId$.pipe(
      distinctUntilChanged(), // Restart the observable chain if the space id changes
      switchMap(() => this.isAutomaticCloudRecordingEnabled$),
      switchMap((automaticCloudRecordingEnabled) => {
        if (automaticCloudRecordingEnabled) {
          return this.isAnyoneRecording$.pipe(
            // Emit only the first true value, after that complete the observable chain
            filter((isAnyoneRecording) => isAnyoneRecording === true),
            first(),
            startWith(false),
          );
        } else {
          return this.isAnyoneRecording$;
        }
      }),
    );

    combineLatest([
      this.featureManagementService.isAutomaticRecordingCapabilityEnabled$,
      this.spaceRepo.activeSpaceId$.pipe(filterNil()),
    ])
      .pipe(
        map(
          ([isAutomaticRecordingCapabilityEnabled, _]) =>
            isAutomaticRecordingCapabilityEnabled &&
            !this.spaceRepo.activeSpace?.settings?.disableAlwaysOnRecording &&
            this.spaceRepo.activeSpace?.institution?.settings?.crRecordingMode ===
              RecordingMode.AUTOMATIC,
        ),
        distinctUntilChanged(),
      )
      .subscribe(this.isAutomaticCloudRecordingEnabledSubject);

    // Initialize the privacy aware cloud recording subject
    combineLatest([
      this.capabilityTierService.shouldEnableCapability$(Capability.PRIVACY_AWARE_CLOUD_RECORDING),
      this.spaceRepo.activeSpaceId$.pipe(filterNil()),
    ])
      .pipe(
        map(
          ([isPrivacyAwareCloudRecordingCapabilityEnabled, _]) =>
            isPrivacyAwareCloudRecordingCapabilityEnabled &&
            this.spaceRepo.activeSpace?.institution?.settings
              ?.crEnablePrivacyAwareCloudRecording === true,
        ),
      )
      .subscribe(this.isPrivacyAwareCloudRecordingEnabledSubject);
  }

  // ---- Public Methods ----
  public async stopRecording(): Promise<void> {
    await this.currentService.stopRecording();
    this._didCurrentUserEndRecording$.next(true);
  }

  /** Marks the next finished local recording to be auto-downloaded instead of prompting. */
  public flagAutoDownloadNextLocalRecording(): void {
    this._autoDownloadNextLocalRecording = true;
  }

  /** Reads and clears the auto-download flag; true means download the finished recording silently. */
  public consumeAutoDownloadNextLocalRecording(): boolean {
    const shouldAutoDownload = this._autoDownloadNextLocalRecording;
    this._autoDownloadNextLocalRecording = false;
    return shouldAutoDownload;
  }

  public pauseRecording(): void {
    if (PauseableRecordingService.isPauseable(this.currentService)) {
      this.currentService.pauseRecording();
    } else {
      throw new Error('Current service does not support pausing');
    }
  }

  public resumeRecording(): void {
    if (PauseableRecordingService.isPauseable(this.currentService)) {
      this.currentService.resumeRecording();
    } else {
      throw new Error('Current service does not support resuming');
    }
  }

  public isRecordingInProgress(): boolean {
    return this._isRecordingInProgress(this.status);
  }

  public clearRecordingOutput(): void {
    this._recordingOutput$.next({ _type: 'none' });
  }

  public switchService(service: RECORDING_SERVICES): void {
    if (this.isRecordingInProgress()) {
      throw new Error('Cannot switch recording service while recording is in progress');
    }

    this.currentRecorder$.next(service);
  }

  public isCurrentRecordingLocal(): boolean {
    return this.currentRecorder$.getValue() === RECORDING_SERVICES.LOCAL;
  }

  public cloudRecordingActive(): boolean {
    return this.cloudRecordingService.state$.getValue()._type === RECORD_STATUS.ACTIVE;
  }

  public supportPrivacyCloudRecording(): boolean {
    return (
      this.browserDetectionService.supportCaptureHandleConfig() &&
      this.browserDetectionService.supportElementCapture()
    );
  }

  // ---- Private Methods ---- //
  private async _startRecording(): Promise<void> {
    this.currentService.startRecording();
  }

  public startLocalRecordingWithOptions(options?: { preferCurrentTab?: boolean }): void {
    this.switchService(RECORDING_SERVICES.LOCAL);
    if (this.localRecordingService && 'startRecordingWithOptions' in this.localRecordingService) {
      (this.localRecordingService as any).startRecordingWithOptions(options);
    } else {
      this._startRecording();
    }
  }

  private _isRecordingInProgress(status: RECORDING_STATE) {
    return status._type === RECORD_STATUS.ACTIVE || status._type === RECORD_STATUS.PAUSED;
  }

  private handleRecordingStatusChange(status: RECORDING_STATE) {
    if (
      this.currentRecorder$.getValue() === RECORDING_SERVICES.LOCAL &&
      this.spaceRepo.activeSpaceId &&
      this.spaceRepo.activeSpaceCurrentRoomUid
    ) {
      switch (status._type) {
        case RECORD_STATUS.ACTIVE:
          this.presenceProvider.setRecordingPresence(
            this.spaceRepo.activeSpaceId,
            this.spaceRepo.activeSpaceCurrentRoomUid,
          );
          break;
        case RECORD_STATUS.FINISHED:
          this.presenceProvider.removeRecordingPresence(
            this.spaceRepo.activeSpaceId,
            this.spaceRepo.activeSpaceCurrentRoomUid,
          );
          break;
        default:
          break;
      }
    }
  }

  private get status() {
    return this.status$.getValue();
  }

  private get currentService() {
    const service = this.services.get(this.currentRecorder$.getValue());
    if (!service) {
      throw new Error(`Expected ${this.currentRecorder$.getValue()} to be in the services map`);
    }
    return service;
  }

  private _canUserControlRecordingManually(space: ISession, user: User) {
    const { event } = space;

    // If the recording mode is configured to use participant egress, then the user (Host or participant) can control the recording
    if (
      space.institution?.settings?.crAdvancedSettings?.egressType ===
      Collaboration.Spaces.EgressType.PARTICIPANT
    ) {
      return true;
    }

    if (this.isAutomaticCloudRecordingEnabled) {
      return false;
    }
    if (event) {
      return Boolean(event.host_ids?.includes(user._id));
    }

    return Session.isOwnedByUser(space, user);
  }

  public startRecording(service: RECORDING_SERVICES): void {
    this.switchService(service);
    this._startRecording();
  }

  async changeRecordingAccess(recording: SpaceRecording, access: RecordingAccess) {
    if (recording.access === access || (!recording && access === RecordingAccess.HOSTS)) {
      return;
    }

    try {
      await firstValueFrom(this.spacesService.updateRecordingAccess(recording.recordingId, access));
      this.notificationToasterService.showDefaultSuccessNotification(
        this.translateService.instant('Recording access changed'),
        SUCCESSES.RECORDING_CHANGE_ACCESS,
      );
      return true;
    } catch (error) {
      this.telemetry.errorEvent('recording_access_changed_failed', {
        err: error,
        message: error.message,
        name: error.name,
      });
      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translateService.instant('Recording access change failed'),
        this.translateService.instant('Please try again'),
        ERRORS.RECORDING_ACCESS_CHANGE_FAILED,
        undefined,
        undefined,
        5,
      );
      return false;
    }
  }

  updateRecordingSummary(recordingId: string, summary: RecordingSummary): Observable<boolean> {
    return this.spacesService.updateRecordingSummary(recordingId, summary).pipe(
      tap(() => {
        this.notificationToasterService.showDefaultSuccessNotification(
          this.translateService.instant('Recording summary updated'),
          SUCCESSES.RECORDING_SUMMARY_UPDATED,
        );
      }),
      map(() => true),
      catchError((error) => {
        this.notificationToasterService.showDefaultErrorNotificationV2(
          this.translateService.instant('Recording summary update failed'),
          this.translateService.instant('Please try again'),
          ERRORS.RECORDING_SUMMARY_UPDATE_FAILED,
          undefined,
          undefined,
          5,
        );
        return of(false);
      }),
    );
  }

  copyLink(recording: SpaceRecording): void {
    const link = `${location.origin}/recording/${recording.recordingId}`;
    navigator.clipboard.writeText(link);
    this.notificationToasterService.showDefaultSuccessNotification(
      this.translateService.instant('Link copied to clipboard'),
      SUCCESSES.RECORDING_COPY_LINK,
    );
  }

  async downloadRecording(recording: SpaceRecording): Promise<void> {
    this._isDownloading$.next(true);
    try {
      await this.fetchSignedUrlAndDownload(recording);
    } finally {
      this._isDownloading$.next(false);
    }
  }

  async downloadRecordings(recordings: SpaceRecording[]): Promise<void> {
    if (recordings.length === 0) {
      return;
    }

    this._isDownloading$.next(true);
    try {
      await Promise.all(recordings.map((recording) => this.fetchSignedUrlAndDownload(recording)));
    } finally {
      this._isDownloading$.next(false);
    }
  }

  private async fetchSignedUrlAndDownload(recording: SpaceRecording): Promise<void> {
    const signedUrlResponse = await firstValueFrom(
      this.spacesService.getSignedRecordingUrl(recording.recordingId),
    );

    await this.performRecordingDownload(signedUrlResponse.downloadLink);
  }

  private async performRecordingDownload(downloadLink: string): Promise<void> {
    try {
      await this.triggerBrowserDownload(downloadLink);
      this.notificationToasterService.showDefaultSuccessNotification(
        this.translateService.instant('Recording Downloaded'),
        SUCCESSES.RECORDING_DOWNLOADED,
      );
    } catch (error) {
      this.telemetry.errorEvent('download_recording_failed', {
        err: error,
        message: error.message,
        name: error.name,
      });
      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translateService.instant('Recording download failed'),
        this.translateService.instant('Please try again'),
        ERRORS.RECORDING_DOWNLOAD_FAILED,
        undefined,
        undefined,
        5,
      );
    }
  }

  private triggerBrowserDownload(url: string): Promise<void> {
    return this.browserDownloadMutex.runExclusive(async () => {
      const downloadLinkElement = document.createElement('a');
      downloadLinkElement.href = url;
      downloadLinkElement.rel = 'noopener';
      downloadLinkElement.setAttribute('download', '');
      downloadLinkElement.style.display = 'none';
      document.body.appendChild(downloadLinkElement);
      downloadLinkElement.click();
      await new Promise<void>((resolve) =>
        modifiedSetTimeout(resolve, BROWSER_DOWNLOAD_THROTTLE_MS, true),
      );
      downloadLinkElement.remove();
    });
  }

  /**
   * Formats a Date object to YYYYMMDD_HHmmSS string
   */
  private formatDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}_${hours}${minutes}${seconds}`;
  }

  /**
   * Formats seconds into a human-readable timestamp (m:ss or h:mm:ss)
   */
  private formatSecondsToTimestamp(secondsFromStart: number): string {
    const hours = Math.floor(secondsFromStart / 3600);
    const minutes = Math.floor((secondsFromStart % 3600) / 60);
    const seconds = secondsFromStart % 60;

    // Format timestamp conditionally based on hours
    if (hours === 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    }
  }

  /**
   * Formats transcript data for text file export, merging consecutive entries from the same user
   */
  private formatTranscriptDataForText(recording: SpaceRecording): string {
    const usersMap: Map<string, string> = new Map();
    for (const user of recording.users ?? []) {
      const id = user?._id;
      if (!id) {
        continue;
      }
      usersMap.set(String(id), user?.name ?? 'Unknown');
    }
    let resultText = '';

    // Create header with recording info
    const startTime = new Date(recording.startingTime);
    const durationInMinutes = Math.floor(recording.duration / 60);
    const header =
      `${recording.spaceName} - ${startTime.toLocaleDateString()}\n\n` +
      `Duration: ${durationInMinutes} mins`;
    resultText = header;

    let prevUserId = '';
    let currentTimestamp = '';
    let currentUserName = '';

    const transcriptItems = recording.transcript ?? [];
    for (const item of transcriptItems) {
      // first line or new speaker
      const itemUserId = item.userId ?? '';
      if (!prevUserId || prevUserId !== itemUserId) {
        prevUserId = itemUserId;
        currentTimestamp = this.formatSecondsToTimestamp(Math.floor(item.start));
        currentUserName = (itemUserId && usersMap.get(itemUserId)) || 'Unknown';
        resultText += `\n\n@${currentTimestamp} - ${currentUserName}\n`;
      }
      // add the current text line
      resultText += `${item.text} `;
    }

    return resultText;
  }

  async downloadRecordingTranscript(recording: SpaceRecording): Promise<void> {
    this._isDownloadingTranscript$.next(true);
    try {
      // Format transcript data for text file
      const transcriptText = this.formatTranscriptDataForText(recording);

      // Create and download text file
      const startTime = new Date(recording.startingTime);
      const endTime = new Date(startTime.getTime() + recording.duration * 1000);

      // Generate filename with the format: spaceName-YYYYMMDD_HHmmSS-YYYYMMDD_HHmmSS
      const startDateTime = this.formatDateTime(startTime);
      const endDateTime = this.formatDateTime(endTime);

      const fileName = `${recording.spaceName}-${startDateTime}-${endDateTime}.txt`;

      const blob = new Blob([transcriptText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.notificationToasterService.showDefaultSuccessNotification(
        this.translateService.instant('Recording Transcript Downloaded'),
        SUCCESSES.RECORDING_DOWNLOADED,
      );
    } catch (error) {
      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translateService.instant('No transcript available'),
        this.translateService.instant(
          'Your transcript may not be ready or could not be generated. <a target="_blank" href="https://helpdesk.pencilspaces.com/en/articles/11202345-why-is-there-no-transcript-available">Learn more</a>',
        ),
        ERRORS.RECORDING_DOWNLOAD_FAILED,
        undefined,
        undefined,
        5,
        true,
      );
    } finally {
      this._isDownloadingTranscript$.next(false);
    }
  }

  deleteRecording(spaceId: string, recordingId: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.modalManagerService.showModal(
        ConfirmationModalComponent,
        {
          data: {
            title: this.translateService.instant('Delete recording?'),
            description: this.translateService.instant(
              'Are you sure you want to delete this recording? This action cannot be undone',
            ),
            confirmButtonConfig: {
              icon: 'delete_outline',
              iconType: 'mat-icon',
              label: this.translateService.instant('Delete'),
              danger: true,
            },
            cancelButtonConfig: {
              icon: 'close',
              iconType: 'mat-icon',
              label: this.translateService.instant('Cancel'),
            },
          },
        },
        {
          afterClosed: async (res) => {
            if (res === 'confirm') {
              try {
                await firstValueFrom(this.spacesService.deleteRecording(spaceId, recordingId));
                this.notificationToasterService.showDefaultSuccessNotification(
                  this.translateService.instant('Your recording was successfully deleted.'),
                  SUCCESSES.RECORDING_DELETED,
                );
                resolve(true);
              } catch (error) {
                this.telemetry.errorEvent('delete_recording_failed', {
                  err: error,
                  message: error.message,
                  name: error.name,
                });
                this.notificationToasterService.showDefaultErrorNotificationV2(
                  this.translateService.instant('Recording deletion failed'),
                  this.translateService.instant('Please try again'),
                  ERRORS.RECORDING_DELETION_FAILED,
                  undefined,
                  undefined,
                  5,
                );
                resolve(false);
              }
            } else {
              resolve(false);
            }
          },
        },
      );
    });
  }

  public setNavigationSource(source: RECORDING_DETAILS_NAVIGATION_SOURCE | undefined): void {
    this._recordingDetailsNavigationSource$.next(source);
  }

  public determineRecordingPermissions(
    user: User | undefined,
    downloadCapabilities: DownloadCapabilities,
    space: ISession | undefined,
  ): RecordingViewPermissions {
    const defaultPermissions: RecordingViewPermissions = {
      download: false,
      downloadTranscript: false,
      delete: false,
      changeRecordingAccess: false,
      editSummary: false,
      publicAccessEnabled: false,
      adminAccessEnabled: false,
    };
    if (!space || !user) {
      return defaultPermissions;
    }
    const userIsHost = this.spaceRepo.isSpaceOwnerOrHost(user, space);
    const userHasAdminPermissions = this.spaceRepo.hasSpaceAdminPermissions(user, space, true);
    const spaceBelongsToInstitution = !!(space.institutionID ?? space.institution);
    if (!spaceBelongsToInstitution) {
      return {
        download: userIsHost,
        downloadTranscript: userIsHost,
        publicAccessEnabled: userIsHost,
        adminAccessEnabled: false,
        delete: userIsHost,
        changeRecordingAccess: userIsHost,
        editSummary: userIsHost,
      };
    }
    const institutionSettings = space.institution?.settings;
    if (!institutionSettings) {
      return defaultPermissions;
    }
    // Recording and transcript share the same role rule and differ only in the capability
    // that gates them.
    const userCanDownload =
      userHasAdminPermissions || (userIsHost && institutionSettings.crEnableDownloads);
    return {
      download: userCanDownload && downloadCapabilities.recording,
      downloadTranscript: userCanDownload && downloadCapabilities.transcript,
      publicAccessEnabled:
        (userHasAdminPermissions || userIsHost) && institutionSettings.crEnablePublicRecordings,
      adminAccessEnabled: userHasAdminPermissions,
      delete: userHasAdminPermissions || (userIsHost && institutionSettings.crEnableHostDeletion),
      changeRecordingAccess:
        !institutionSettings.crForceAdminOnlyAccess && (userHasAdminPermissions || userIsHost),
      editSummary: userHasAdminPermissions || userIsHost,
    };
  }

  public setRecordingDetailsViewActive(isActive: boolean): void {
    this._recordingDetailsViewActive$.next(isActive);
  }

  public triggerUpdateRecordingsList(updatedRecordings: RecordingAccessUpdate[]): void {
    this.updatedRecordingsListSubject$.next(updatedRecordings);
  }

  public async saveRecordingLocally(
    file: File,
    recordingId: string,
    shouldDeleteFromIndexedDB: boolean,
  ): Promise<void> {
    try {
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.style.display = 'none';
      const url = window.URL.createObjectURL(file);
      a.href = url;
      let fileExt = 'webm';
      if (file.type.split('/')[1]) {
        fileExt = file.type.split('/')[1];
      }
      a.download = `${file.name}.${fileExt}`;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Defer post-download cleanup until the next app open.
      if (shouldDeleteFromIndexedDB) {
        await markRecordingForDeletionOnNextOpen(recordingId);
      }
    } catch (error) {
      this.telemetry.errorEvent('automatic_save_recording_locally_failed', {
        err: error,
        message: error.message,
        name: error.name,
      });
    }
  }
}
